import { getPaginatedReviews, markReviewAsResolved, createFeedbackNote } from '@app/services/reviews';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { BusinessReviews } from '@app/models/reviews';
import { toast } from 'react-toastify';

interface FeedbackReviews {
  reviews: BusinessReviews[];
  count: number;
  pages: number;
}

export const useRatingsFeedback = () => {
  const [activeTab, setActiveTab] = useState<string>('all');
  const [activeReview, setActiveReview] = useState<string | undefined>(
    undefined
  );
  
  const filtersForm = useForm({
    defaultValues: {
      sort: 'newest',
      start: '',
      end: '',
      search: '',
      limit: 10,
      page: 1,
    },
  });

  // Watch form values for changes
  const startDate = filtersForm.watch('start') as string;
  const endDate = filtersForm.watch('end') as string;
  const sortBy = filtersForm.watch('sort') as string;

  // Fetch Echo reviews with 1-2 star ratings only
  const {
    data: feedbackReviewsData,
    isLoading,
    refetch,
    error,
  } = useQuery({
    queryKey: ['feedback-reviews', startDate, endDate, sortBy],
    queryFn: async () => {
      const limit = (filtersForm.getValues('limit') as number) ?? 10;
      const pageValue = Number((filtersForm.getValues('page') as number) ?? 1);
      const page = Number(pageValue === 0 ? 1 : pageValue);
      const search = (filtersForm.getValues('search') as string) ?? '';

      // Map sortBy to API params
      let sortByApi: 'createdAt' | 'customer' | 'photos' | 'rating' | undefined = undefined;
      let order: 'ASC' | 'DESC' | undefined = undefined;
      if (sortBy) {
        switch (sortBy) {
          case 'newest':
            sortByApi = 'createdAt';
            order = 'DESC';
            break;
          case 'older':
            sortByApi = 'createdAt';
            order = 'ASC';
            break;
          default:
            sortByApi = 'createdAt';
            order = 'DESC';
        }
      }

      return getPaginatedReviews({
        text: search,
        limit,
        page,
        startDate,
        endDate,
        minRating: 1, // Only 1-2 star reviews
        maxRating: 2,
        sortBy: sortByApi,
        order,
        isFeedback: true, // Only show feedback reviews
      });
    },
  });

  const feedbackData: FeedbackReviews = {
    reviews: feedbackReviewsData?.data?.reviews?.map((review: any) => ({
      ...review,
      platform: 'echo', // All reviews are from Echo
    })) || [],
    count: feedbackReviewsData?.data?.count || 0,
    pages: feedbackReviewsData?.data?.pages || 1,
  };

  const queryClient = useQueryClient();

  // Mutation for marking review as resolved
  const resolveMutation = useMutation({
    mutationFn: ({ reviewId, resolved }: { reviewId: string; resolved: boolean }) =>
      markReviewAsResolved(reviewId, resolved),
    onSuccess: () => {
      // Invalidate and refetch the feedback reviews query
      queryClient.invalidateQueries({ queryKey: ['feedback-reviews'] });
    },
    onError: (error) => {
      console.error('Error marking review as resolved:', error);
      toast.error('Failed to mark review as resolved');
    },
  });

  const handleReviewClick = (id: string) => {
    setActiveReview(id);
  };

  const handleResolveReview = async (reviewId: string, resolved: boolean, data?: { sendFollowUp: boolean; notes?: string }) => {
    try {
      // First, mark the review as resolved
      await resolveMutation.mutateAsync({ reviewId, resolved });
      
      // If notes were provided, create a note
      if (data?.notes && data.notes.trim()) {
        await createFeedbackNote(reviewId, data.notes, data.sendFollowUp);
      } else if (data?.sendFollowUp) {
        // If no notes but follow-up campaign is enabled, create an empty note with createCampaign flag
        await createFeedbackNote(reviewId, '', data.sendFollowUp);
      } 

      // Check if the current active review should be cleared based on the new state
      if (activeReview === reviewId) {
        const newFilteredReviews = getFilteredReviews();
        if (!newFilteredReviews.find(review => review.id === reviewId)) {
          setActiveReview(undefined);
        }
      }

      // Return success to close the modal
      return { success: true };
    } catch (error) {
      console.error('Error resolving review:', error);
      toast.error('Failed to resolve review');
      // Return error to keep modal open
      return { success: false, error };
    }
  };

  // Helper function to determine if a review is new (within last 7 days)
  const isNewFeedback = (date: string) => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return new Date(date) > sevenDaysAgo;
  };

  // Filter reviews based on active tab
  const getFilteredReviews = () => {
    const reviews = feedbackData.reviews;
    
    switch (activeTab) {
      case 'all':
        // Show all reviews
        return reviews;
      case 'new-feedback':
        // Filter for new feedback (within last 7 days)
        return reviews.filter(review => isNewFeedback(review.createdAt));
      case 'needs-resolution':
        // Filter for reviews that need resolution (not resolved)
        return reviews.filter(review => !review.resolved);
      case 'resolved':
        // Filter for resolved reviews (resolved = true)
        return reviews.filter(review => review.resolved);
      default:
        return reviews; // Default to all reviews
    }
  };

  const filteredReviews = getFilteredReviews();

  const tabOptions = [
    {
      label: 'All',
      value: 'all',
      count: feedbackData.reviews.length,
    },
    {
      label: 'New feedback',
      value: 'new-feedback',
      count: feedbackData.reviews.filter(review => isNewFeedback(review.createdAt)).length,
    },
    {
      label: 'Needs a resolution',
      value: 'needs-resolution',
      count: feedbackData.reviews.filter(review => !review.resolved).length,
    },
    {
      label: 'Resolved',
      value: 'resolved',
      count: feedbackData.reviews.filter(review => review.resolved).length,
    },
  ];

  const handleTabClick = (value: string) => {
    setActiveTab(value);
    
    // Check if current active review exists in the new filtered list
    const newFilteredReviews = (() => {
      const reviews = feedbackData.reviews;
      switch (value) {
        case 'all':
          return reviews;
        case 'new-feedback':
          return reviews.filter(review => isNewFeedback(review.createdAt));
        case 'needs-resolution':
          return reviews.filter(review => !review.resolved);
        case 'resolved':
          return reviews.filter(review => review.resolved);
        default:
          return reviews;
      }
    })();
    
    // If current active review is not in the new filtered list, clear it
    if (activeReview && !newFilteredReviews.find(review => review.id === activeReview)) {
      setActiveReview(undefined);
    }
  };

  return {
    requestData: { data: { ...feedbackData, reviews: filteredReviews } },
    isLoading,
    refetch,
    error,
    filtersForm,
    handleReviewClick,
    activeReview,
    tabOptions,
    handleTabClick,
    activeTab,
    handleResolveReview,
    isResolving: resolveMutation.isPending,
  };
}; 